import { GetNotifications200Response } from "../../codegen-api";

/**
 * Which `notifications` frames belong to the trader on screen (FE-7).
 *
 * Any `notifications:*` frame used to be merged into the current cache, so after
 * a wallet switch (or a server-side room leak) wallet A's fills were listed and
 * toasted under wallet B. The backend stamps every frame with the lowercased
 * `account` it was emitted for; a frame is accepted only when that account (or,
 * failing that, an address in the channel name) is the current account, and
 * only rows for the active chain and this account survive. A frame naming no
 * account at all is refused.
 */

export type NotificationFrame =
  | { kind: "snapshot"; items: GetNotifications200Response[] }
  | { kind: "live"; item: GetNotifications200Response };

const ADDRESS = /^0x[a-f0-9]{40}$/i;

const lower = (value: unknown) =>
  typeof value === "string" && value.trim() ? value.trim().toLowerCase() : undefined;

const chainOf = (value: any) => value?.chain_id ?? value?.chainId;

const chainMatches = (chainId: unknown, activeChainId?: string) =>
  chainId === undefined ||
  chainId === null ||
  chainId === "" ||
  !activeChainId ||
  String(chainId) === activeChainId;

export const readNotificationFrame = (
  frame: unknown,
  accountKey: string | undefined,
  activeChainId?: string
): NotificationFrame | undefined => {
  const record = frame && typeof frame === "object" ? (frame as any) : undefined;
  const account = lower(accountKey);
  if (!record || !account || typeof record.channel !== "string") return undefined;

  const [prefix, ...segments] = record.channel.split(":");
  if (prefix !== "notifications") return undefined;

  // The backend room, and so the channel, is `notifications:{chainId}:{account}`.
  const frameAccount =
    lower(record.account) ?? lower(segments.find((s: string) => ADDRESS.test(s)));
  if (frameAccount !== account) return undefined;
  const channelChain = segments.find((s: string) => /^\d+$/.test(s));
  if (!chainMatches(chainOf(record) ?? channelChain, activeChainId)) return undefined;

  const belongs = (row: unknown): row is GetNotifications200Response => {
    if (!row || typeof row !== "object") return false;
    const rowAccount = lower((row as any).account);
    return (
      (rowAccount === undefined || rowAccount === account) &&
      chainMatches(chainOf(row), activeChainId)
    );
  };

  const { data } = record;
  if (!data || typeof data !== "object" || Array.isArray(data)) return undefined;
  if ("snapshot" in data) {
    return Array.isArray(data.snapshot)
      ? { kind: "snapshot", items: data.snapshot.filter(belongs) }
      : undefined;
  }
  return belongs(data) ? { kind: "live", item: data } : undefined;
};
